import { h, Component, Host, State, Element, Prop, Watch, Fragment, Listen, Event, EventEmitter } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { MultiContentStageSchemeDefinition, MultiContentStageSchemeName } from "./constants/multi-content-stage-types";
import {
  MULTI_CONTENT_STAGE_SCHEME_CONFIG,
  MULTI_CONTENT_STAGE_SCHEME_MAP
} from "./constants/multi-content-stage-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { isMobile, isTablet } from "../../utils/screen-helper";
import { isFirefox } from "../../utils/device-helper";
import { MultiContentStageSchemeConfiguration } from "../../constants/colors";
import { getNestedChildren } from "../../utils/dom-helper";
import { isTouchDevice } from "../../utils/touch-helper";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 * @slot [static-background-image] - holds one eon-ui-image as static background.
 */
@Component({
  tag: "eon-ui-multi-content-stage",
  styleUrl: "multi-content-stage.scss",
  shadow: true
})
export class MultiContentStage
  implements ColorableComponent<MultiContentStageSchemeConfiguration<MultiContentStageSchemeDefinition>> {
  schemeConfig: MultiContentStageSchemeConfiguration<MultiContentStageSchemeDefinition> = MULTI_CONTENT_STAGE_SCHEME_CONFIG;
  stagesWrapper!: HTMLElement;
  wrapper!: HTMLElement;
  contentWrapper!: HTMLElement;
  wrapperFrame!: HTMLElement;
  autoslider: any;
  initialTimeout: any;
  staticBackground: HTMLElement;
  schemeMap = MULTI_CONTENT_STAGE_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: MultiContentStageSchemeDefinition;

  /**
   * Collection on stages
   */
  @State() stages: Element[] = [];

  /**
   * Width of one stage tile
   */
  @State() stagesWidth: number;

  /**
   * Visibility of the left slider control button
   */
  @State() leftControlVisible: boolean;

  /**
   * Visibility of the right slider control button
   */
  @State() rightControlVisible: boolean;

  /**
   * Position of slider movable wrapper relative to container
   */
  @State() sliderPosition: number = 0;

  /**
   * Number of available stages
   */
  @State() stagesCount: number;

  /**
   * Number of visible stage tiles per view, 4 per default
   */
  @State() slidesPerView: number = 4;

  /**
   * Margin between slides
   */
  @State() slidesMargin: number;

  /**
   * Absolute width of movable stages wrapper
   */
  @State() stagesWrapperWidth: string;

  /**
   * Is slider swipable for touchscreens or controls should be displayed
   */
  @State() isSwipeble: boolean;

  /**
   * Detects big screens to show oversize wrapper
   */
  @State() wrapperOversize: boolean;

  /**
   * Height of the background image
   */
  @State() backgroundSize: number;

  /**
   * Turn on/off slider autochange
   */
  @State() internalAutoSlider: boolean = false;

  /**
   * Fast forward animation
   */
  @State() fastForwardAnimation: boolean = false;

  /**
   * Size of the promo-flag (if found)
   */
  @State() promoFlagSize: "small" | "normal";

  /**
   * Is slider activated or not
   */
  @Prop({ mutable: true }) isSlider: boolean;

  /**
   * Slider autochange timer
   */
  @Prop() sliderTimer: number;

  /**
   * Turn on/off slider autochange
   */
  @Prop({ mutable: true }) autoSlider: boolean;

  /**
   * Blur background
   */
  @Prop() blurBackground: boolean;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: MultiContentStageSchemeName;

  /**
   * Sets this to a valid zero-based index of a stage to select it.
   */
  @Prop({ reflect: true, mutable: true }) activeStageIndex: number = 0;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  @Element() hostElement: HTMLEonUiMultiContentStageElement;

  /**
   * Stores the name of current viewport
   */
  @State() currentScreenSize: number;

  /**
   * Raised when the mcs has been loaded.
   */
  @Event() mcsLoad: EventEmitter;

  /**
   * Raised when one of the mcs items has been clicked or auto play is interrupted.
   */
  @Event() mcsItemClick: EventEmitter<number>;

  componentWillLoad() {
    this.applyScheme();
    this.stages = Array.from(this.hostElement.getElementsByTagName("EON-UI-MULTI-CONTENT-STAGE-ITEM"));
    this.stagesCount = this.stages.length;
    this.staticBackground = this.hostElement.querySelector('[slot="static-background-image"]');
    this.selectStage();
    this.isSwipeble = isTouchDevice();
    this.getPromoFlagSize();
  }

  componentDidLoad() {
    this.slidesMargin =
      parseInt(window.getComputedStyle(this.stagesWrapper.querySelector(".eonui-mcs-item")).marginRight, 10) * 2 || 16;
    this.setCssVariables();
    this.handleAutoSlider();
    this.resizeHandler();
    this.imageVisibility();
    this.mcsLoad.emit();
  }

  imageVisibility() {
    const isImageAvailable = Array.from(getNestedChildren(this.hostElement, "eon-ui-image"));
    isImageAvailable.forEach((imageItem) => {
      if (imageItem.slot === "stage-item-image" || imageItem.slot === "stage-item-background") {
        imageItem.addEventListener("imageSrcLoaded", () => {
          this.resizeHandler();
        });
      }
    });
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.hostElement.style.setProperty("--animationTime", `${this.sliderTimer}ms`);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("activeStageIndex")
  selectStage(_newValue?) {
    if (this.activeStageIndex < 0) this.activeStageIndex = this.stagesCount - 1;
    if (this.activeStageIndex >= this.stagesCount) this.activeStageIndex = 0;

    this.stages.forEach((stage, index) =>
      stage.setAttribute("is-active", (index === this.activeStageIndex).toString())
    );

    window.setTimeout(() => {
      const stageElements = this.stagesWrapper.querySelectorAll(".eonui-mcs-item");
      const newActiveStageElement = stageElements[this.activeStageIndex];
      // const nextStageElement = stageElements[this.activeStageIndex + 1];
      // const previousStageElement = stageElements[this.activeStageIndex + 1];

      let stageElementPosition = this.getElemenInContainerPosition(newActiveStageElement, this.wrapper);

      if (!stageElementPosition.inView) {
        if (stageElementPosition.outOfViewRight) {
          // this.sliderPosition so that right hand side of element is on right edge
          this.sliderPosition =
            -(this.stagesWidth + this.slidesMargin) * (this.activeStageIndex + 1 - this.slidesPerView);
        } else if (stageElementPosition.outOfViewLeft) {
          // this.sliderPosition so that left hand side of element is on left edge
          this.sliderPosition = -(this.stagesWidth + this.slidesMargin) * this.activeStageIndex;
        }
      } /* else {
        // Maybe todo: if there is a next or previous slide out of view, scroll one further
        // collides with mobile behaviour
        if (!!nextStageElement && !this.getElemenInContainerPosition(nextStageElement, this.wrapperFrame).inView) {
          this.changeSlide("next");
        } else if (
          !!previousStageElement &&
          !this.getElemenInContainerPosition(previousStageElement, this.wrapperFrame).inView
        ) {
          this.changeSlide("prev");
        }
      } */
    }, 0);
  }

  private getPromoFlagSize() {
    const slottedPromoFlags = Array.from(getNestedChildren(this.hostElement, "eon-ui-promo-flag"));

    if (slottedPromoFlags.length) {
      let smallCount = 0;

      slottedPromoFlags.forEach((item) => {
        item.getAttribute("small") ? smallCount++ : false;
      });

      this.promoFlagSize = smallCount === slottedPromoFlags.length ? "small" : "normal";
    }
  }

  getElemenInContainerPosition(element, container) {
    let elementPos = element.getBoundingClientRect();
    let containerPos = container.getBoundingClientRect();
    let relativePos = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      inView: false,
      outOfViewTop: false,
      outOfViewRight: false,
      outOfViewBottom: false,
      outOfViewLeft: false
    };

    relativePos.top = elementPos.top - containerPos.top;
    relativePos.right = elementPos.right - containerPos.right;
    relativePos.bottom = elementPos.bottom - containerPos.bottom;
    relativePos.left = elementPos.left - containerPos.left;
    relativePos.inView =
      relativePos.top >= 0 && relativePos.left >= 0 && relativePos.bottom <= 0 && relativePos.right <= 0;
    relativePos.outOfViewTop = relativePos.top < 0 ? true : false;
    relativePos.outOfViewLeft = relativePos.left < 0 ? true : false;
    relativePos.outOfViewBottom = relativePos.bottom > 0 ? true : false;
    relativePos.outOfViewRight = relativePos.right > 0 ? true : false;

    return relativePos;
  }

  @Watch("autoSlider")
  handleAutoSlider() {
    if (this.autoSlider) {
      // this.initialTimeout = setTimeout(() => {
      this.internalAutoSlider = true;
      this.autoslider = window.setInterval(() => {
        this.activeStageIndex < this.stagesCount - 1
          ? (this.activeStageIndex = this.activeStageIndex + 1)
          : (this.activeStageIndex = 0);
      }, this.sliderTimer);
      // }, 2000);
    } else {
      setTimeout(() => {
        this.internalAutoSlider = false;
      }, 500);
      this.initialTimeout = clearTimeout(this.initialTimeout);
    }
  }

  @Listen("resize", { target: "window" })
  resizeHandler() {
    const oldScreenSize = this.currentScreenSize;
    this.currentScreenSize = window.innerWidth;

    if (oldScreenSize === this.currentScreenSize) {
      return;
    }

    this.destroySlider();
    let resizeTimeout;
    window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(() => {
      this.wrapperOversize = false;
      if (isMobile()) {
        this.slidesPerView = 1;
      } else if (isTablet()) {
        this.slidesPerView = 2;
      } else if (window.innerWidth > (1920 / 4) * 5) {
        this.slidesPerView = 5;
        this.wrapperOversize = true;
      } else {
        this.slidesPerView = 4;
      }

      this.slidesPerView < this.stagesCount ? this.initSlider() : this.destroySlider();
      if (this.slidesPerView === 5) {
        this.stagesWidth = (this.wrapper.offsetWidth - this.slidesMargin * (this.slidesPerView - 1)) / 4;
        this.stagesWrapperWidth = `${
          this.stagesCount * this.stagesWidth + (this.stagesCount - 1) * this.slidesMargin
        }px`;
      }
    }, 250);
  }

  private initSlider() {
    if (isMobile()) {
      this.stagesWidth = (this.wrapper.offsetWidth - this.slidesMargin * (this.slidesPerView - 1)) / this.slidesPerView;
    } else if (isTablet()) {
      this.stagesWidth = (this.wrapper.offsetWidth - this.slidesMargin * (this.slidesPerView - 1)) / this.slidesPerView;
    } else {
      this.stagesWidth = (this.wrapper.offsetWidth - this.slidesMargin * (this.slidesPerView - 1)) / this.slidesPerView;
    }

    this.stagesWrapperWidth = `${this.stagesCount * this.stagesWidth + (this.stagesCount - 1) * this.slidesMargin}px`;
    this.isSlider = true;
  }

  private destroySlider() {
    this.sliderPosition = 0;
    this.stagesWidth = undefined;
    this.stagesWrapperWidth = "auto";
    this.isSlider = false;
  }

  private changeSlide(direction) {
    if (direction === "next") {
      this.sliderPosition -= this.stagesWidth + this.slidesMargin;
    } else if (direction === "prev") {
      this.sliderPosition += this.stagesWidth + this.slidesMargin;
    }
  }

  private handleClick(index) {
    this.activateItem(index);
    this.mcsItemClick.emit(this.activeStageIndex);
  }

  private handleFocus(index) {
    this.activateItem(index);
  }

  private activateItem(index) {
    this.fastForwardAnimation = true;
    this.activeStageIndex = index;
    clearInterval(this.autoslider);
    this.autoSlider = false;
  }

  private detectSwipe(event) {
    if (this.isSwipeble) {
      // event.preventDefault();

      clearInterval(this.autoslider);
      this.autoSlider = false;
      const target = event.target;
      const touchstartX = event.changedTouches[0].screenX;
      target.addEventListener(
        "touchend",
        (e) => {
          const touchendX = e.changedTouches[0].screenX;

          if (Math.abs(touchendX - touchstartX) > 100) {
            if (touchendX > touchstartX) {
              this.activeStageIndex -= 1;
            } else {
              this.activeStageIndex += 1;
            }
          }
        },
        { once: true }
      );
    }
  }

  render() {
    return (
      <Host>
        <div class={pfx("mcs")}>
          <div class={pfx("mcs-content-group")}>
            <div
              class={pfx({
                "mcs-wrapper": true,
                oversize: this.wrapperOversize,
                [`has-promo-flag-${this.promoFlagSize}`]: this.promoFlagSize
              })}
            >
              <div class={pfx("mcs-background")} style={{ height: `${this.backgroundSize}px` }}>
                {!this.staticBackground &&
                  this.stages.map((stage: Element, index: number) => {
                    return (
                      <div
                        class={pfx({
                          "mcs-background-item": true,
                          active: index === this.activeStageIndex,
                          blurred: isFirefox() && this.blurBackground // fix for firefox that does not support backdrop-filter blur
                        })}
                        innerHTML={stage.querySelector('[slot="stage-item-background"]')?.outerHTML}
                      ></div>
                    );
                  })}
                {!!this.staticBackground && (
                  <div
                    class={pfx({
                      "mcs-background-item": true,
                      active: true,
                      blurred: isFirefox() && this.blurBackground // fix for firefox that does not support backdrop-filter blur
                    })}
                    innerHTML={this.staticBackground.outerHTML}
                  ></div>
                )}
                {this.blurBackground && <div class={pfx("mcs-background-blur")}></div>}
              </div>
              <div class={pfx("mcs-stages-wrapper-frame")} ref={(el) => (this.wrapper = el as HTMLElement)}>
                <div
                  class={pfx({
                    "mcs-stages-wrapper": true,
                    "is-slider": this.isSlider,
                    autoslider: this.internalAutoSlider,
                    [`stages-${this.stagesCount}`]: true,
                    "no-blur": !this.blurBackground,
                    "no-blur-ff": !this.blurBackground && isFirefox()
                  })}
                  style={{ left: `${this.sliderPosition}px`, width: this.stagesWrapperWidth }}
                  ref={(el) => (this.stagesWrapper = el as HTMLElement)}
                  onTouchStart={(event) => this.detectSwipe(event)}
                >
                  {this.stages.map((stage: Element, index: number) => {
                    const img = !!stage.querySelector('[slot="stage-item-image"]');
                    return (
                      <div
                        role="button"
                        tabindex="0"
                        class={pfx({
                          "mcs-item": true,
                          "no-image": !img,
                          active: index === this.activeStageIndex
                        })}
                        onClick={() => this.handleClick(index)}
                        onFocus={() => this.handleFocus(index)}
                        onKeyDown={() => this.handleFocus(index)}
                        style={{ width: `${this.stagesWidth}px` }}
                      >
                        {img && (
                          <div class={pfx("mcs-item-image")}>
                            <div class={pfx("mcs-item-image-inner")}>
                              <div class={pfx("mcs-loader")}>
                                <div
                                  class={pfx({ "mcs-loader-element": true, "fast-forward": this.fastForwardAnimation })}
                                ></div>
                              </div>
                              <div innerHTML={stage.querySelector('[slot="stage-item-image"]')?.outerHTML}></div>
                            </div>
                          </div>
                        )}
                        <div class={pfx("mcs-item-description")}>
                          <div class={pfx("mcs-item-description-background")}>
                            {!img && (
                              <div class={pfx("mcs-loader")}>
                                <div
                                  class={pfx({ "mcs-loader-element": true, "fast-forward": this.fastForwardAnimation })}
                                ></div>
                              </div>
                            )}
                          </div>
                          <div class={pfx("mcs-item-description-inner")}>
                            <div class={pfx("mcs-item-headline")}>
                              {stage.getAttribute("icon") && (
                                <eon-ui-icon
                                  class={pfx("icon")}
                                  size="small"
                                  name={stage.getAttribute("icon")}
                                  aria-hidden="true"
                                ></eon-ui-icon>
                              )}
                              {stage.getAttribute("headline")}
                            </div>
                            <div class={pfx("mcs-item-text")}>{stage.getAttribute("text")}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {this.isSlider && (
                  <Fragment>
                    <div
                      class={pfx({
                        "mcs-control": true,
                        "mcs-control-left": true,
                        "mcs-control-visible": this.sliderPosition < 0
                      })}
                      role="button"
                      onClick={() => this.changeSlide("prev")}
                      onKeyDown={() => this.changeSlide("prev")}
                      tabindex="0"
                    >
                      <div class={pfx("icon-carrier")}>
                        <eon-ui-icon
                          class={pfx({
                            "icon-content": true
                          })}
                          name={"chevron_bold_left"}
                          alignment-x="center"
                          alignment-y="center"
                        ></eon-ui-icon>
                      </div>
                    </div>
                    <div
                      class={pfx({
                        "mcs-control": true,
                        "mcs-control-right": true,
                        "mcs-control-visible":
                          -this.sliderPosition + this.wrapper.offsetWidth < parseInt(this.stagesWrapperWidth, 10)
                      })}
                      role="button"
                      onClick={() => this.changeSlide("next")}
                      onKeyDown={() => this.changeSlide("next")}
                      tabindex="0"
                    >
                      <div class={pfx("icon-carrier")}>
                        <eon-ui-icon
                          class={pfx({
                            "icon-content": true
                          })}
                          name={"chevron_bold_right"}
                          alignment-x="center"
                          alignment-y="center"
                        ></eon-ui-icon>
                      </div>
                    </div>
                  </Fragment>
                )}
              </div>
            </div>
            {/* Element is not focusable, so no need to add a keyDown listener;
            We should listen for a user interaction even though element is not interactable (because the content will be) */}
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events */}
            <div
              class={pfx("mcs-content-wrapper")}
              ref={(el) => (this.contentWrapper = el as HTMLElement)}
              onClick={() => {
                if (this.autoSlider) this.handleClick(this.activeStageIndex);
              }}
              role="contentinfo"
            >
              <slot />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
